import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import CandidateDashboardLayout from '../components/layouts/CandidateDashboardLayout';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { API_URL } from '../config';
import CourseCertificate from '../components/CourseCertificate';

const CourseProgressPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progressData, setProgressData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCertificate, setShowCertificate] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pin, setPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [certificateUnlocked, setCertificateUnlocked] = useState(false);
  
  const candidateSession = SessionManager.getSession('candidate');
  const token = candidateSession?.access_token;
  const candidateId = candidateSession?.user_id;

  useEffect(() => {
    if (!candidateSession) {
      navigate('/login');
      return;
    }
    loadCourseProgress();
  }, [courseId]);

  const loadCourseProgress = async () => {
    try {
      setLoading(true);
      
      // Fetch course details
      const courseResponse = await fetch(`${API_URL}/courses/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (courseResponse.ok) {
        const courseData = await courseResponse.json();
        setCourse(courseData);
      }
      
      // Fetch progress summary
      const progressResponse = await fetch(
        `${API_URL}/course-teaching/progress/summary/${courseId}?candidate_id=${candidateId}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (progressResponse.ok) {
        const progress = await progressResponse.json();
        console.log('Progress data received:', progress);
        setProgressData(progress);
      } else {
        console.error('Failed to fetch progress:', progressResponse.status);
      }
    } catch (error) {
      console.error('Error loading course progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-700 border-green-200';
      case 'in_progress': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'paused': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'skipped': return 'bg-orange-100 text-orange-700 border-orange-200';
      default: return 'bg-gray-50 text-gray-500 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'check_circle';
      case 'in_progress': return 'play_circle';
      case 'paused': return 'pause_circle';
      case 'skipped': return 'fast_forward';
      default: return 'radio_button_unchecked';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Completed';
      case 'in_progress': return 'In Progress';
      case 'paused': return 'Paused';
      case 'skipped': return 'Skipped';
      default: return 'Not Started';
    }
  };

  const handleContinueModule = (module) => {
    const moduleId = module.module_id || `module_${module.module_number}`;
    if (module.status === 'paused') {
      navigate(`/course/${courseId}/module/${moduleId}/teach?resume=true`);
    } else {
      navigate(`/course/${courseId}/module/${moduleId}/teach`);
    }
  };

  const calculateOverallProgress = () => {
    if (!progressData) return 0;
    return progressData.overall_completion_percentage || 0;
  };

  const handleViewCertificate = () => {
    const progress = calculateOverallProgress();
    if (progress === 100) {
      // All modules completed - unlock certificate
      setCertificateUnlocked(true);
      setShowCertificate(true);
    } else {
      // Show PIN modal for admin testing
      setShowPinModal(true);
    }
  };

  const handlePinSubmit = () => {
    if (pin === 'viewcourse@123') {
      setPinError('');
      setShowPinModal(false);
      setCertificateUnlocked(true);
      setShowCertificate(true);
      setPin('');
    } else {
      setPinError('Invalid PIN. Please complete all modules or enter the correct PIN.');
    }
  };

  const handleDownloadCertificate = () => {
    window.print();
  };

  if (loading) {
    return (
      <CandidateDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  const overallProgress = calculateOverallProgress();

  return (
    <CandidateDashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/candidate/courses')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <Icon name="arrow_back" size={20} />
            <span>Back to My Courses</span>
          </button>
          
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">{course?.title}</h1>
                <p className="text-gray-600 mb-4">{course?.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-600">
                  <span className="flex items-center gap-1">
                    <Icon name="schedule" size={16} />
                    {course?.duration}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="signal_cellular_alt" size={16} />
                    {course?.level}
                  </span>
                  <span className="flex items-center gap-1">
                    <Icon name="category" size={16} />
                    {course?.category}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Overview */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Overall Progress</span>
              <Icon name="trending_up" className="text-teal-600" size={20} />
            </div>
            <p className="text-3xl font-bold text-teal-600">{overallProgress}%</p>
            <div className="w-full bg-gray-200 rounded-full h-2 mt-3">
              <div 
                className="bg-teal-600 h-2 rounded-full transition-all duration-300" 
                style={{ width: `${overallProgress}%` }}
              ></div>
            </div>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Completed</span>
              <Icon name="check_circle" className="text-green-600" size={20} />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {progressData?.completed_modules || 0}
              <span className="text-lg text-gray-500">/{progressData?.total_modules || 0}</span>
            </p>
            <p className="text-xs text-gray-500 mt-1">modules</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">In Progress</span>
              <Icon name="play_circle" className="text-blue-600" size={20} />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {progressData?.in_progress_modules || 0}
            </p>
            <p className="text-xs text-gray-500 mt-1">modules</p>
          </div>

          <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-2">
              <span className="text-gray-600 text-sm">Remaining</span>
              <Icon name="pending" className="text-gray-600" size={20} />
            </div>
            <p className="text-3xl font-bold text-gray-900">
              {progressData?.remaining_modules ?? progressData?.locked_modules ?? 0}
            </p>
            <p className="text-xs text-gray-500 mt-1">modules</p>
          </div>
        </div>

        {/* Module List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Course Modules</h2>
          
          <div className="space-y-4">
            {!progressData?.modules || progressData.modules.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Icon name="school" size={48} className="mx-auto mb-4 text-gray-300" />
                <p>No modules found for this course</p>
              </div>
            ) : (
              progressData.modules.map((module, index) => {
              const isLocked = module.is_locked;
              const status = module.status || 'not_started';
              const moduleData = course?.modules?.[index];
              
              return (
                <div
                  key={module.module_number}
                  className={`border rounded-lg p-5 transition-all ${
                    isLocked 
                      ? 'bg-gray-50 border-gray-200 opacity-60' 
                      : 'bg-white border-gray-200 hover:border-teal-300 hover:shadow-md'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    {/* Module Number Badge */}
                    <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg ${
                      status === 'completed' 
                        ? 'bg-green-100 text-green-700' 
                        : status === 'in_progress' || status === 'paused'
                        ? 'bg-blue-100 text-blue-700'
                        : status === 'skipped'
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-600'
                    }`}>
                      {status === 'completed' ? (
                        <Icon name="check" size={24} />
                      ) : status === 'skipped' ? (
                        <Icon name="fast_forward" size={24} />
                      ) : (
                        module.module_number
                      )}
                    </div>

                    {/* Module Content */}
                    <div className="flex-1">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-1">
                            {moduleData?.title || `Module ${module.module_number}`}
                          </h3>
                          {moduleData?.description && (
                            <p className="text-sm text-gray-600 mb-2">{moduleData.description}</p>
                          )}
                        </div>
                        
                        {/* Status Badge */}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(status)}`}>
                          <Icon name={getStatusIcon(status)} size={14} />
                          {getStatusText(status)}
                        </span>
                      </div>

                      {/* Module Meta Info */}
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        {moduleData?.duration && (
                          <span className="flex items-center gap-1">
                            <Icon name="schedule" size={14} />
                            {moduleData.duration}
                          </span>
                        )}
                        {moduleData?.lessons_details && (
                          <span className="flex items-center gap-1">
                            <Icon name="menu_book" size={14} />
                            {moduleData.lessons_details.length} lessons
                          </span>
                        )}
                        {isLocked && (
                          <span className="flex items-center gap-1 text-gray-500">
                            <Icon name="lock" size={14} />
                            Locked
                          </span>
                        )}
                      </div>

                      {/* Progress Bar for In Progress */}
                      {status === 'in_progress' && (
                        <div className="mb-3">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div className="bg-blue-600 h-2 rounded-full" style={{ width: '45%' }}></div>
                          </div>
                        </div>
                      )}

                      {/* Action Buttons */}
                      {!isLocked && (
                        <div className="flex items-center gap-2">
                          {status === 'completed' ? (
                            <button
                              onClick={() => handleContinueModule(module)}
                              className="px-4 py-2 text-sm font-medium text-teal-600 hover:bg-teal-50 rounded-lg transition-colors flex items-center gap-1"
                            >
                              <Icon name="replay" size={16} />
                              Review Module
                            </button>
                          ) : status === 'paused' ? (
                            <button
                              onClick={() => handleContinueModule(module)}
                              className="px-4 py-2 text-sm font-medium bg-blue-600 text-white hover:bg-blue-700 rounded-lg transition-colors flex items-center gap-1"
                            >
                              <Icon name="play_arrow" size={16} />
                              Resume Learning
                            </button>
                          ) : status === 'in_progress' ? (
                            <button
                              onClick={() => handleContinueModule(module)}
                              className="px-4 py-2 text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 rounded-lg transition-colors flex items-center gap-1"
                            >
                              <Icon name="play_arrow" size={16} />
                              Continue
                            </button>
                          ) : (
                            <button
                              onClick={() => handleContinueModule(module)}
                              className="px-4 py-2 text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 rounded-lg transition-colors flex items-center gap-1"
                            >
                              <Icon name="play_arrow" size={16} />
                              Start Module
                            </button>
                          )}
                          
                          {status !== 'completed' && status !== 'not_started' && (
                            <button
                              onClick={() => navigate(`/courses/${courseId}`)}
                              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              View Details
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
            )}
          </div>
        </div>

        {/* Certificate Section */}
        <div className="mt-6 bg-gradient-to-r from-teal-50 to-blue-50 rounded-xl shadow-sm border border-teal-200 p-6">
          <div className="flex items-center gap-4">
            <div className="flex-shrink-0 w-16 h-16 bg-teal-600 rounded-full flex items-center justify-center">
              <Icon name="workspace_premium" className="text-white" size={32} />
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-1">
                {overallProgress === 100 ? 'Congratulations!' : 'Course Certificate'}
              </h3>
              <p className="text-gray-600">
                {overallProgress === 100 
                  ? "You've completed all modules. View your certificate now!" 
                  : `Complete all modules to unlock your certificate (${overallProgress}% completed)`}
              </p>
            </div>
            <button 
              onClick={handleViewCertificate}
              className={`px-6 py-3 font-semibold rounded-lg transition-colors flex items-center gap-2 ${
                overallProgress === 100
                  ? 'bg-teal-600 text-white hover:bg-teal-700'
                  : 'bg-gray-300 text-gray-600 cursor-not-allowed'
              }`}
            >
              {overallProgress === 100 ? (
                <>
                  <Icon name="workspace_premium" size={20} />
                  View Certificate
                </>
              ) : (
                <>
                  <Icon name="lock" size={20} />
                  Locked
                </>
              )}
            </button>
          </div>
        </div>

        {/* PIN Modal for Admin Testing */}
        {showPinModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Certificate Locked</h2>
                <button 
                  onClick={() => {
                    setShowPinModal(false);
                    setPin('');
                    setPinError('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="close" size={24} />
                </button>
              </div>
              
              <div className="mb-6">
                <div className="flex items-center gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg mb-4">
                  <Icon name="lock" className="text-yellow-600" size={24} />
                  <p className="text-sm text-yellow-800">
                    Complete all course modules to unlock your certificate.
                  </p>
                </div>
                
                <div className="text-center text-sm text-gray-600 mb-4">
                  <p>Current Progress: <span className="font-bold text-teal-600">{overallProgress}%</span></p>
                  <p className="mt-2">Modules Completed: <span className="font-bold">{progressData?.completed_modules || 0}/{progressData?.total_modules || 0}</span></p>
                </div>
                
                <div className="border-t border-gray-200 my-4"></div>
                
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-xs text-gray-500 mb-3 text-center">Admin Testing Only</p>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Enter Admin PIN
                  </label>
                  <input
                    type="password"
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
                    placeholder="Enter PIN"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  {pinError && (
                    <p className="text-red-600 text-sm mt-2">{pinError}</p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setShowPinModal(false);
                    setPin('');
                    setPinError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handlePinSubmit}
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Unlock
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Certificate Modal */}
        {showCertificate && certificateUnlocked && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4 overflow-y-auto">
            <div className="relative max-w-5xl w-full my-8">
              <div className="flex justify-end gap-3 mb-4">
                <button
                  onClick={handleDownloadCertificate}
                  className="px-6 py-3 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors flex items-center gap-2"
                >
                  <Icon name="download" size={20} />
                  Download Certificate
                </button>
                <button
                  onClick={() => setShowCertificate(false)}
                  className="px-6 py-3 bg-gray-600 text-white font-semibold rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
                >
                  <Icon name="close" size={20} />
                  Close
                </button>
              </div>
              
              <CourseCertificate
                candidateName={`${candidateSession?.first_name || ''} ${candidateSession?.last_name || ''}`.trim() || 'Student'}
                courseName={course?.title || 'Course'}
                completionDate={new Date().toISOString()}
                courseId={courseId}
              />
            </div>
          </div>
        )}
      </div>
    </CandidateDashboardLayout>
  );
};

export default CourseProgressPage;
